import InstantSearch from "instantsearch.js/es/lib/InstantSearch.js";
import { useEffect, useMemo, version as ReactVersion } from 'react';
import { useInstantSearchServerContext } from "./useInstantSearchServerContext.js";
import { useInstantSearchSSRContext } from "./useInstantSearchSSRContext.js";
import version from "../version.js";
import { useForceUpdate } from "./useForceUpdate.js";
import { useStableValue } from "./useStableValue.js";
var defaultUserAgents = ["react (".concat(ReactVersion, ")"), "react-instantsearch (".concat(version, ")"), "react-instantsearch-hooks (".concat(version, ")")];
export function useInstantSearch(props) {
  var serverContext = useInstantSearchServerContext();
  var serverState = useInstantSearchSSRContext();
  var stableProps = useStableValue(props);
  var search = useMemo(function () {
    return serverAdapter(new InstantSearch(stableProps), stableProps, serverContext, serverState);
  }, [stableProps, serverContext, serverState]);
  var forceUpdate = useForceUpdate();
  useEffect(function () {
    addAlgoliaAgents(stableProps.searchClient, defaultUserAgents);
  }, [stableProps.searchClient]);
  useEffect(function () {
    // On SSR, the instance is already started so we don't start it again here.
    if (!search.started) {
      search.start();
      forceUpdate();
    }

    return function () {
      search.dispose();
    };
  }, [search, serverState, forceUpdate]);
  return search;
}

function serverAdapter(search, props, serverContext, serverState) {
  var initialResults = serverState === null || serverState === void 0 ? void 0 : serverState.initialResults;

  if (serverContext || initialResults) {
    // InstantSearch.js has a private Initial Results API that lets us inject
    // results on the search instance.
    // On the server, we default the initial results to an empty object so that
    // InstantSearch.js doesn't schedule a search that isn't used, leading to
    // an additional network request. (This is equivalent to monkey-patching
    // `scheduleSearch` to a noop.)
    search._initialResults = initialResults || {}; // On the server, we start the search early to compute the search parameters.
    // On SSR, we start the search early to directly catch up with the lifecycle
    // and render.

    search.start();
  }

  if (serverContext) {
    // On the browser, we add user agents in an effect. Since effects are not
    // run on the server, we need to add user agents directly here.
    addAlgoliaAgents(props.searchClient, [].concat(defaultUserAgents, ["react-instantsearch-server (".concat(version, ")")])); // We notify `getServerState()` of the InstantSearch internals to retrieve
    // the server state and pass it to the render on SSR.

    serverContext.notifyServer({
      search: search
    });
  }

  return search;
}

function addAlgoliaAgents(searchClient, userAgents) {
  if (typeof searchClient.addAlgoliaAgent !== 'function') {
    return;
  }

  userAgents.forEach(function (userAgent) {
    searchClient.addAlgoliaAgent(userAgent);
  });
}